/**
 * Whether any page shell currently has a chat dock mounted.
 *
 * `PassaviaGlobalChat` and the app-shell sidebar button are fallback launchers
 * and must stand down where a shell already owns a dock, or the user sees two
 * chat affordances. Shells publish that upward here — which context cannot
 * express, since it flows the other way — and publishing beats testing a list
 * of paths: a list goes stale the moment a route switches shells, which is
 * exactly how the calculator/program divergence started.
 *
 * A leaf module with no component imports, because `AppChrome` reads it eagerly:
 * hanging this off `useChatDock` would drag the launchers and the panel graph
 * into the initial-route chunk.
 */
import { useSyncExternalStore } from 'react'

let mounted = 0
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

// Stable identities — React re-runs its snapshot check whenever `getSnapshot`
// changes, and all three readers render often.
const getSnapshot = (): boolean => mounted > 0
/** Server/prerender: nothing has mounted before hydration. */
const getServerSnapshot = (): boolean => false

/**
 * Claim the page's chat dock; call the returned release on unmount.
 *
 * A count, not a flag: docks overlap briefly across a route change, when the
 * incoming page registers before the outgoing page's cleanup. Each release
 * fires at most once, so no caller can drive the count negative.
 */
export function registerChatDock(): () => void {
  mounted += 1
  emit()
  let released = false
  return () => {
    if (released) return
    released = true
    mounted -= 1
    emit()
  }
}

/** `true` while a shell owns the page's dock, so fallback launchers hide. */
export function useChatDockMounted(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
